import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Heading,
  Stack,
  Tag,
  Text,
  Textarea,
  VStack,
  useColorMode,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  listFeedback,
  getFeedback,
  addFeedbackComment,
  cancelFeedback,
} from "../store/feedback/thunks";
import { clearSelected } from "../store/feedback/slice";
import ConfirmDialog from "./common/ConfirmDialog";
import DataTable from "./common/DataTable";
import SubmitFeedbackDialog from "./feedback/SubmitFeedbackDialog";
import useToastNotification from "../hooks/useToastNotification";

const STATUS_COLOR = {
  Backlog: "gray",
  "Selected for Development": "blue",
  "Dev In Progress": "blue",
  "Dev Testing": "yellow",
  "Dev Reassigned": "orange",
  "Dev Complete": "teal",
  "QA Assigned": "yellow",
  "QA In Progress": "yellow",
  "QA Complete": "teal",
  "Deployed to Staging": "purple",
  "Deployed to Production": "green",
  "Wont Do": "red",
};

const formatDate = (raw) => {
  if (!raw) return "—";
  try {
    return new Date(raw).toLocaleString();
  } catch (e) {
    return raw;
  }
};

export default function MyFeedback() {
  const dispatch = useDispatch();
  const showToast = useToastNotification();
  const { colorMode } = useColorMode();
  const { list, selected } = useSelector((state) => state.feedback);

  const [reply, setReply] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);

  const refreshList = useCallback(async () => {
    setLoadingList(true);
    try {
      await dispatch(listFeedback({})).unwrap();
    } catch (e) {
      showToast(e?.response?.data?.result || "Could not load your feedback", { status: "error" });
    } finally {
      setLoadingList(false);
    }
  }, [dispatch, showToast]);

  useEffect(() => {
    refreshList();
    return () => {
      dispatch(clearSelected());
    };
  }, [refreshList, dispatch]);

  const openIssue = async (issue_key) => {
    try {
      await dispatch(getFeedback({ issue_key })).unwrap();
    } catch (e) {
      showToast(e?.response?.data?.result || "Could not load this feedback", { status: "error" });
    }
  };

  const closeIssue = () => {
    dispatch(clearSelected());
    setReply("");
  };

  const submitReply = async () => {
    if (!selected || !reply.trim()) return;
    setSubmitting(true);
    try {
      await dispatch(addFeedbackComment({ issue_key: selected.issue_key, body: reply.trim() })).unwrap();
      setReply("");
      showToast("Reply posted", { status: "success" });
    } catch (e) {
      showToast(e?.response?.data?.result || "Could not post your reply", { status: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const onCancel = async () => {
    if (!selected) return;
    setCancelling(true);
    try {
      await dispatch(cancelFeedback({ issue_key: selected.issue_key })).unwrap();
      showToast("Feedback cancelled", { status: "success" });
      setConfirmCancel(false);
    } catch (e) {
      showToast(e?.response?.data?.result || "Could not cancel this feedback", { status: "error" });
    } finally {
      setCancelling(false);
    }
  };

  const columns = [
    {
      key: "issue_key",
      header: "Ticket",
      render: (row) => (
        <Button variant="link" colorScheme="blue" onClick={() => openIssue(row.issue_key)}>
          {row.issue_key}
        </Button>
      ),
    },
    { key: "summary", header: "Summary" },
    { key: "issue_type", header: "Type" },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <Tag colorScheme={STATUS_COLOR[row.status] || "gray"} size="sm">
          {row.status}
        </Tag>
      ),
    },
    { key: "created", header: "Created", render: (row) => formatDate(row.created) },
    { key: "updated", header: "Updated", render: (row) => formatDate(row.updated) },
  ];

  return (
    <Flex
      flexDirection="column"
      width="100wh"
      minHeight="100vh"
      backgroundColor={colorMode === "light" ? "gray.200" : "gray.800"}
      justifyContent="flex-start"
      alignItems="center"
      overflow="auto"
      pt="20px"
      pb="40px"
    >
      <Box width={{ base: "95%", md: "900px" }} p={4} bg={colorMode === "light" ? "white" : "gray.700"} rounded="lg" boxShadow="sm">
        <HStack justifyContent="space-between" mb={4}>
          <Heading size="md">My Feedback</Heading>
          <HStack>
            <Button size="sm" colorScheme="blue" onClick={() => setSubmitDialogOpen(true)}>
              Submit New Feedback
            </Button>
            <Button size="sm" onClick={refreshList} isLoading={loadingList}>Refresh</Button>
          </HStack>
        </HStack>

        <Text fontSize="sm" color="gray.500" mb={3}>
          Submissions are tracked live in our issue tracker. Reply to add context — cancel a submission while it's still in {`"Backlog"`}.
        </Text>

        <DataTable
          columns={columns}
          data={list?.issues || []}
          emptyMessage={loadingList ? "Loading…" : "No feedback submitted yet"}
        />
      </Box>

      <ConfirmDialog
        isOpen={!!selected}
        onClose={closeIssue}
        title={selected ? `${selected.issue_key} — ${selected.summary || ""}` : ""}
        confirmLabel="Close"
        cancelLabel="Close"
        size="xl"
      >
        {selected && (
          <Stack spacing={3}>
            <HStack>
              <Tag colorScheme={STATUS_COLOR[selected.status] || "gray"}>{selected.status}</Tag>
              <Text fontSize="sm" color="gray.500">{selected.issue_type}</Text>
            </HStack>
            <Box>
              <Text fontWeight="bold" mb={1}>Description</Text>
              <Box
                p={3}
                bg={colorMode === "light" ? "gray.50" : "gray.800"}
                rounded="md"
                whiteSpace="pre-wrap"
                fontSize="sm"
              >
                {selected.description || "—"}
              </Box>
            </Box>
            <Box>
              <Text fontWeight="bold" mb={1}>Conversation ({(selected.comments || []).length})</Text>
              <VStack align="stretch" spacing={2} maxH="240px" overflowY="auto">
                {(selected.comments || []).length === 0 ? (
                  <Text fontSize="sm" color="gray.500">No replies yet.</Text>
                ) : (
                  (selected.comments || []).map((c) => (
                    <Box
                      key={c.id}
                      p={2}
                      bg={colorMode === "light" ? "gray.50" : "gray.800"}
                      rounded="md"
                      fontSize="sm"
                    >
                      <Text fontWeight="bold">{c.author || "Support"}</Text>
                      <Text fontSize="xs" color="gray.500">{formatDate(c.created)}</Text>
                      <Text whiteSpace="pre-wrap" mt={1}>{c.body}</Text>
                    </Box>
                  ))
                )}
              </VStack>
            </Box>
            <Box>
              <Text fontWeight="bold" mb={1}>Add a Reply</Text>
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={3}
                placeholder="Add additional context for the team…"
                maxLength={5000}
              />
              <HStack justifyContent="space-between" mt={2}>
                {selected.can_cancel ? (
                  <Button colorScheme="red" variant="outline" size="sm" onClick={() => setConfirmCancel(true)}>
                    Cancel This Feedback
                  </Button>
                ) : <Box />}
                <Button colorScheme="blue" size="sm" onClick={submitReply} isDisabled={!reply.trim()} isLoading={submitting}>
                  Send Reply
                </Button>
              </HStack>
            </Box>
          </Stack>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        onConfirm={onCancel}
        title="Cancel this feedback?"
        confirmLabel="Cancel Feedback"
        confirmColorScheme="red"
        isLoading={cancelling}
      >
        <Text>Cancelling will close this ticket. You won't be able to undo this.</Text>
      </ConfirmDialog>

      <SubmitFeedbackDialog
        isOpen={submitDialogOpen}
        onClose={() => setSubmitDialogOpen(false)}
        showDeepLink={false}
        onSubmitSuccess={() => refreshList()}
      />
    </Flex>
  );
}
